import React, { useEffect } from 'react';
import {
  Users, Flag, Scale, DollarSign,
  TrendingUp, Server, Gamepad2, Activity,
  AlertTriangle, AlertCircle, Info, Loader2, RefreshCw,
  Clock, ArrowRight,
} from 'lucide-react';
import { useAdminStore } from '../../store/adminStore';

/* ── Helpers ── */
const fmt = (n: number | undefined | null) => (n ?? 0).toLocaleString('fr-CA');
const fmtMoney = (n: number | undefined | null) => `${(n ?? 0).toFixed(2)} $`;
const timeAgo = (d: string) => {
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return 'À l\'instant';
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
};

/* ═══════════════════════════════════════════════════════════════════════════ */

const AdminDashboard: React.FC = () => {
  const { dashboardStats: stats, isLoadingDashboard, fetchDashboard, setSection } = useAdminStore();

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (isLoadingDashboard && !stats) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-admin-accent" />
      </div>
    );
  }

  if (!stats) return null;

  const alerts = stats.alerts || [];
  const hasUrgent = (stats.moderation?.pendingReports ?? 0) > 0 || (stats.moderation?.pendingAppeals ?? 0) > 0;

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-[1100px] mx-auto p-6 space-y-5">

        {/* ── Header row ── */}
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-text-primary">Dashboard</h1>
          <button
            onClick={fetchDashboard}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-highlight/50 text-xs transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoadingDashboard ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* ── Alerts ── */}
        {alerts.length > 0 && (
          <div className="space-y-1.5">
            {alerts.map((a, i) => {
              const isErr = a.level === 'critical';
              const isWarn = a.level === 'warning';
              const Icon = isErr ? AlertTriangle : isWarn ? AlertCircle : Info;
              return (
                <div key={i} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium ${
                  isErr ? 'bg-error/8 text-error' : isWarn ? 'bg-warning/8 text-warning' : 'bg-info/8 text-info'
                }`}>
                  <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                  {a.message}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Top KPI strip ── */}
        <div className="grid grid-cols-5 gap-px bg-border-color/30 rounded-xl overflow-hidden">
          {[
            { label: 'Utilisateurs', value: fmt(stats.users?.total), sub: `${fmt(stats.users?.online)} en ligne`, color: 'text-admin-accent' },
            { label: 'Revenu total', value: fmtMoney(stats.billing?.totalRevenue), sub: `Auj: ${fmtMoney(stats.billing?.revenueToday)}`, color: 'text-success' },
            { label: 'Abonnements', value: fmt(stats.billing?.activeSubscriptions), sub: `${fmt(stats.billing?.totalTransactions)} tx`, color: 'text-info' },
            { label: 'Serveurs', value: fmt(stats.servers?.total), sub: `${fmt(stats.servers?.banned)} bannis`, color: 'text-text-primary' },
            { label: 'Jeux', value: fmt(stats.games?.total), sub: `${fmt(stats.games?.pendingReview)} en revue`, color: 'text-gaming-accent' },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-bg-secondary/60 px-4 py-3.5 flex flex-col">
              <span className="text-[10px] font-medium text-text-muted uppercase tracking-wider">{kpi.label}</span>
              <span className={`text-lg font-bold tabular-nums mt-0.5 ${kpi.color}`}>{kpi.value}</span>
              <span className="text-[10px] text-text-muted mt-0.5">{kpi.sub}</span>
            </div>
          ))}
        </div>

        {/* ── Two-column layout ── */}
        <div className="grid grid-cols-5 gap-4">

          {/* LEFT: 3 cols — moderation + revenue detail */}
          <div className="col-span-3 space-y-4">

            {/* Moderation queue */}
            {hasUrgent && (
              <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
                <h2 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider mb-3">File de modération</h2>
                <div className="space-y-1.5">
                  {(stats.moderation?.pendingReports ?? 0) > 0 && (
                    <button
                      onClick={() => setSection('reports')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-error/5 hover:bg-error/10 transition-colors text-left group"
                    >
                      <Flag className="h-4 w-4 text-error" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-text-primary">{fmt(stats.moderation?.pendingReports)} signalement{(stats.moderation?.pendingReports ?? 0) > 1 ? 's' : ''} en attente</p>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  )}
                  {(stats.moderation?.pendingAppeals ?? 0) > 0 && (
                    <button
                      onClick={() => setSection('appeals')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-warning/5 hover:bg-warning/10 transition-colors text-left group"
                    >
                      <Scale className="h-4 w-4 text-warning" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-text-primary">{fmt(stats.moderation?.pendingAppeals)} contestation{(stats.moderation?.pendingAppeals ?? 0) > 1 ? 's' : ''} en attente</p>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Revenue breakdown */}
            <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider">Revenus</h2>
                <button onClick={() => setSection('billing')} className="text-[11px] text-admin-accent hover:text-admin-accent-hover transition-colors">
                  Voir tout →
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Aujourd\'hui', value: fmtMoney(stats.billing?.revenueToday), icon: TrendingUp },
                  { label: '7 jours', value: fmtMoney(stats.billing?.revenue7d), icon: DollarSign },
                  { label: '30 jours', value: fmtMoney(stats.billing?.revenue30d), icon: DollarSign },
                ].map((r) => (
                  <div key={r.label} className="bg-bg-tertiary/40 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-[10px] text-text-muted mb-1">
                      <r.icon className="h-3 w-3" /> {r.label}
                    </div>
                    <p className="text-base font-bold text-success tabular-nums">{r.value}</p>
                  </div>
                ))}
              </div>
              {(stats.billing?.totalRefunded ?? 0) > 0 && (
                <p className="text-[10px] text-text-muted mt-3">Remboursé au total: <span className="text-error font-medium">{fmtMoney(stats.billing.totalRefunded)}</span></p>
              )}
            </div>

            {/* Quick stats row */}
            <div className="grid grid-cols-3 gap-3">
              <button onClick={() => setSection('users')} className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-3 hover:border-admin-accent/30 transition-colors text-left">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-3.5 w-3.5 text-admin-accent" />
                  <span className="text-[10px] font-medium text-text-muted uppercase tracking-wider">Nouveaux (24h)</span>
                </div>
                <p className="text-xl font-bold text-text-primary tabular-nums">{fmt(stats.users?.new24h)}</p>
                <p className="text-[10px] text-text-muted mt-0.5">7j: {fmt(stats.users?.new7d)} · 30j: {fmt(stats.users?.new30d)}</p>
              </button>
              <button onClick={() => setSection('servers')} className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-3 hover:border-admin-accent/30 transition-colors text-left">
                <div className="flex items-center gap-2 mb-2">
                  <Server className="h-3.5 w-3.5 text-info" />
                  <span className="text-[10px] font-medium text-text-muted uppercase tracking-wider">Serveurs</span>
                </div>
                <p className="text-xl font-bold text-text-primary tabular-nums">{fmt(stats.servers?.total)}</p>
                <p className="text-[10px] text-text-muted mt-0.5">{fmt(stats.servers?.banned)} bannis</p>
              </button>
              <button onClick={() => setSection('games')} className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-3 hover:border-admin-accent/30 transition-colors text-left">
                <div className="flex items-center gap-2 mb-2">
                  <Gamepad2 className="h-3.5 w-3.5 text-gaming-accent" />
                  <span className="text-[10px] font-medium text-text-muted uppercase tracking-wider">Jeux</span>
                </div>
                <p className="text-xl font-bold text-text-primary tabular-nums">{fmt(stats.games?.total)}</p>
                <p className="text-[10px] text-text-muted mt-0.5">{fmt(stats.games?.pendingReview)} en revue</p>
              </button>
            </div>
          </div>

          {/* RIGHT: 2 cols — moderation stats */}
          <div className="col-span-2">

            {/* Moderation summary */}
            <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
              <h2 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider mb-3">Modération</h2>
              <div className="space-y-1">
                {[
                  { label: 'Actions aujourd\'hui', value: fmt(stats.moderation?.actionsToday), icon: Activity, cls: 'text-warning' },
                  { label: 'Utilisateurs bannis', value: fmt(stats.users?.banned), icon: Flag, cls: 'text-error' },
                  { label: 'Suspendus', value: fmt(stats.users?.suspended), icon: Clock, cls: 'text-warning' },
                ].map((row) => (
                  <div key={row.label} className="flex items-center gap-3 py-2 border-b border-border-color/20 last:border-0">
                    <row.icon className={`h-3.5 w-3.5 flex-shrink-0 ${row.cls}`} />
                    <span className="text-xs text-text-secondary flex-1">{row.label}</span>
                    <span className="text-sm font-semibold text-text-primary tabular-nums">{row.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-3 pt-3 border-t border-border-color/30 space-y-1">
                <h3 className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">File d'attente</h3>
                {[
                  { label: 'Signalements en attente', value: fmt(stats.moderation?.pendingReports), icon: Flag, cls: 'text-error', section: 'reports' as const },
                  { label: 'Contestations en attente', value: fmt(stats.moderation?.pendingAppeals), icon: Scale, cls: 'text-warning', section: 'appeals' as const },
                  { label: 'Jeux en revue', value: fmt(stats.games?.pendingReview), icon: Gamepad2, cls: 'text-gaming-accent', section: 'games' as const },
                ].map((row) => (
                  <button
                    key={row.label}
                    onClick={() => setSection(row.section)}
                    className="w-full flex items-center gap-3 py-2 hover:bg-highlight/30 px-2 -mx-2 rounded-lg transition-colors group"
                  >
                    <row.icon className={`h-3.5 w-3.5 flex-shrink-0 ${row.cls}`} />
                    <span className="text-xs text-text-secondary flex-1 text-left">{row.label}</span>
                    <span className="text-sm font-semibold text-text-primary tabular-nums">{row.value}</span>
                    <ArrowRight className="h-3 w-3 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;
